import React, { useState } from 'react';

import ChevronDown from '@gravity-ui/icons/ChevronDown';
import Plus from '@gravity-ui/icons/Plus';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button, Icon, SegmentedRadioGroup, Select, TextArea, TextInput, withToaster } from '@gravity-ui/uikit';
import { lengthConfig, validateActivationPhrase, validateEmail, validateName, validateURL } from '@paskills/validation';
import i18n from '@yandex-int/i18n';
import { updateInstallableAppDraft } from 'client/api';
import { InstallableAppControlPanel } from 'client/blocks/ControlPanel/InstallableAppControlPanel';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { BackgroundImageUploader } from 'client/blocks/FilesUploaders/BackgroundImageUploader';
import { FileUploaderInput } from 'client/blocks/FilesUploaders/FilesUploaders';
import { InstallableAppLogoUploader } from 'client/blocks/FilesUploaders/InstallableAppLogoUploader';
import { LogoUploader } from 'client/blocks/FilesUploaders/LogoUploader';
import { ApkFormFields } from 'client/blocks/form/ApkFormFields/ApkFormFields';
import { BrandVerification } from 'client/blocks/form/BrandVerification/BrandVerification';
import { EmailField } from 'client/blocks/form/EmailField/EmailField';
import { Field } from 'client/blocks/form/Field/Field';
import { FormCard } from 'client/blocks/form/FormCard/FormCard';
import { InstallableAppAccessField } from 'client/blocks/form/InstallableAppAccessField/InstallableAppAccessField';
import { ScreenshotsBlock } from 'client/blocks/form/ScreenShots/ScreenshotsBlock';
import { VoiceControl } from 'client/blocks/form/VoiceControl/VoiceControl';
import { Link } from 'client/blocks/Link/Link';
import { ModeratorNotes } from 'client/blocks/statuses/Notes/ModeratorNotes';
import { WithAppsMetaInfo, withAppsMetaInfo } from 'client/context/appsMetaInfoContext';
import formContainer, { FormContainerProps } from 'client/hocs/form-container';
import { InjectedFormProps } from 'client/hocs/types';
import { wrapInText } from 'client/lib/i18n-utils';
import { InstallableAppSettings, SkillAccess } from 'client/model/settings';
import { Channel, InstallableApp } from 'client/model/skill';
import { AgeRestriction, ApkInfoWithTargets, InstallableAppFormSettings } from 'client/types';
import { getCanEditApp, getCanViewAsAdmin, getIsUserPorg } from 'client/utils';
import { createUpdateError, getStringFieldErrorMessage } from 'client/utils/errorMessage';
import {
    getSquareLogoForm,
    mapInstallableAppSettingsToValues,
    mapValuesToInstallableAppSettings,
} from 'client/utils/settings/installable-app';
import { installableAppLabels as l } from 'client/utils/settings/labels';
import { addToast } from 'client/utils/settings/toaster';
import * as utils from 'client/utils/settings/utils';
import * as keyset from 'i18n/AppForm';
import * as TvAppIndexKeyset from 'i18n/AppsIndex';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as settingsErrorsKeyset from 'i18n/SettingsErrors';

import styles from './Settings.module.scss';

interface OuterProps extends WithAppsMetaInfo {
    skillUseZora: boolean;
    disabled: boolean;
    isDraft: boolean;
    handleSaveDraft?: (e?: React.FormEvent) => Promise<void>;
    settings: InstallableAppSettings;
    app: InstallableApp;
    setInstallableApp?: (app: InstallableApp) => void;
}

type Props = InjectedFormProps<InstallableAppFormSettings, OuterProps>;

const t = i18n(keyset);
const tGeneralSettings = i18n(generalSettingsKeyset);
const tTvAppIndex = i18n(TvAppIndexKeyset);
const tError = i18n(settingsErrorsKeyset);

const MONETIZATION_DOCS_URL = 'https://yandex.ru/dev/dialogs/';

const InstallableAppSettingFields: React.FC<Props> = (props) => {
    const {
        values,
        errors,
        isDraft,
        onChange,
        app,
        appsMetaInfo,
        setStateAndValidate,
        isNonValidationError,
        disabled,
        isSubmitting,
        touched,
        setErrors,
    } = props;

    const [openCardName, setOpenCardName] = useState('');

    const categories = appsMetaInfo.categories;
    const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.app.developerType);
    const isUserAdmin = getCanViewAsAdmin();

    const fromDisabled = disabled || isSubmitting || !getCanEditApp(app);

    const handleMobileCardClick = (tabName: string) => {
        if (tabName === openCardName) {
            setOpenCardName('');
        } else {
            setOpenCardName(tabName);
        }
    };

    return (
        <ErrorBoundary withHomepageLink>
            <div>
                <div className={styles.header}>
                    <InstallableAppControlPanel
                        settingValues={values}
                        isSubmitting={isSubmitting}
                        onSettingValuesChange={onChange}
                        settingFormErrors={errors}
                        setErrors={setErrors}
                        isNonValidationError={isNonValidationError}
                        appName={app.onAir ? app.name : app.draft.name}
                        logoUrl={getSquareLogoForm(app, isDraft)}
                        values={values}
                    />
                    {isDraft && <ModeratorNotes />}
                </div>
                <div className={styles.mobileFormHeader}>
                    <ChevronDown />
                    {tTvAppIndex('main-information')}
                </div>
                <FormCard
                    cardKey="about"
                    cardTitle={t('about')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        required
                        fieldTitle={t('app-name-title')}
                        fieldDescription={t('app-name-description')}
                        error={errors.name}
                    >
                        <TextInput
                            autoComplete={false}
                            className={styles.formElement}
                            disabled={fromDisabled}
                            onChange={(e) => {
                                const newActivationPhrases = [e.target.value, ...values.activationPhrases.slice(1)];
                                setStateAndValidate(
                                    {
                                        values: {
                                            ...values,
                                            name: e.target.value,
                                            activationPhrases: newActivationPhrases,
                                        },
                                    },
                                    ['name', 'activationPhrases'],
                                );
                            }}
                            placeholder={t('app-name-placeholder')}
                            size="xl"
                            value={values.name}
                        />
                    </Field>
                    <Field
                        fieldTitle={l.activationPhrase}
                        fieldDescription={l.activationPhrases}
                        error={errors.activationPhrases?.[0]}
                    >
                        {values.activationPhrases.map((phraseText, phraseIndex) => (
                            <TextInput
                                className={styles.formElement}
                                errorMessage={errors.activationPhrases?.[phraseIndex]}
                                key={phraseIndex}
                                onChange={(e) => {
                                    const copiedPhrases = [...values.activationPhrases];
                                    copiedPhrases[phraseIndex] = e.target.value;
                                    onChange('activationPhrases')(copiedPhrases);
                                }}
                                value={phraseText}
                                disabled={fromDisabled || phraseIndex === 0}
                                endContent={
                                    phraseIndex > 0 ? (
                                        <Button
                                            onClick={() => {
                                                const updatedPhrases = [...values.activationPhrases];
                                                updatedPhrases.splice(phraseIndex, 1);
                                                onChange('activationPhrases')(updatedPhrases);
                                            }}
                                            view="flat"
                                            disabled={fromDisabled}
                                        >
                                            <Icon data={Xmark} />
                                        </Button>
                                    ) : undefined
                                }
                                size="xl"
                            />
                        ))}
                        <Button
                            className={styles.formButton}
                            onClick={() => onChange('activationPhrases')([...values.activationPhrases, ''])}
                            disabled={fromDisabled || (!validateAsAdmin && values.activationPhrases?.length >= 4)}
                        >
                            <Icon data={Plus} />
                            {t('add')}
                        </Button>
                    </Field>
                    <Field
                        required
                        fieldTitle={t('short-description-title')}
                        fieldDescription={t('short-description-description')}
                        error={errors.secondaryTitle}
                    >
                        <TextInput
                            autoComplete={false}
                            className={styles.formElement}
                            disabled={fromDisabled}
                            onChange={(e) => onChange('secondaryTitle')(e.target.value)}
                            placeholder={t('short-description-placeholder')}
                            size="xl"
                            value={values.secondaryTitle}
                        />
                    </Field>
                    <Field required fieldTitle={t('description-title')} fieldDescription={t('description-description')}>
                        <TextArea
                            className={styles.formElement}
                            disabled={fromDisabled}
                            error={errors.description}
                            note={
                                <span className={styles.grayNote}>
                                    {lengthConfig.description - (values.description?.length || 0)}
                                </span>
                            }
                            rows={4}
                            size="xl"
                            onChange={(e) => onChange('description')(e.target.value)}
                            value={values.description}
                        />
                    </Field>
                    <Field fieldTitle={t('category-title')} required>
                        <Select
                            className={styles.formElement}
                            disabled={fromDisabled}
                            onUpdate={(value) => {
                                onChange('category')(value[0]);
                            }}
                            options={categories.map(({ title }) => ({ value: title, content: title }))}
                            placeholder={t('category-placeholder')}
                            size="xl"
                            value={[values.category]}
                        />
                    </Field>
                    <Field
                        fieldTitle={t('age-restriction-title')}
                        fieldDescription={t('age-restriction-description')}
                        required
                        error={errors.ageRestriction}
                    >
                        <Select
                            className={styles.formElement}
                            disabled={fromDisabled}
                            onUpdate={(value) => {
                                onChange('ageRestriction')(value[0] as AgeRestriction);
                            }}
                            options={appsMetaInfo.ageRestrictions.map((value) => ({ value, content: value }))}
                            placeholder={t('age-restriction-placeholder')}
                            size="xl"
                            value={values.ageRestriction ? [values.ageRestriction] : undefined}
                        />
                    </Field>
                </FormCard>
                <FormCard
                    cardKey="application"
                    cardTitle={t('application')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field fieldTitle={l.developerName}>
                        <TextInput className={styles.formElement} disabled size="xl" value={values.developerName} />
                    </Field>
                    <EmailField
                        disabled={fromDisabled}
                        error={errors.email}
                        onChange={onChange('email')}
                        value={values.email}
                        touched={touched.email}
                        isVerified={values.emailIsVerified}
                    />
                    {getIsUserPorg() ? null : (
                        <BrandVerification
                            brandVerificationWebsite={values.brandVerificationWebsite}
                            brandIsVerified={values.brandIsVerified}
                            onChange={onChange('brandVerificationWebsite')}
                            error={errors.brandVerificationWebsite}
                            disabled={fromDisabled}
                        />
                    )}
                    <InstallableAppAccessField
                        installableAppAccess={values.skillAccess}
                        setInstallableAppAccess={onChange('skillAccess')}
                        disabled={fromDisabled}
                        channel={app.channel}
                    />
                    <VoiceControl
                        disabled={fromDisabled}
                        skillAccess={values.skillAccess}
                        voiceControlEnable={values.voiceControlEnable}
                        isAdvancedVoiceControl={values.isAdvancedVoiceControlSupported}
                        onVoiceControlChange={onChange('voiceControlEnable')}
                        onAdvancedVoiceControlChange={onChange('isAdvancedVoiceControlSupported')}
                    />
                    <Field
                        fieldTitle={t('has-monetization')}
                        fieldDescription={
                            <span>
                                {wrapInText(
                                    (s: string) => (
                                        <Link
                                            to={MONETIZATION_DOCS_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            theme="info"
                                        >
                                            {s}
                                        </Link>
                                    ),
                                    (s: string) => <span>{s}</span>,
                                )(t('has-monetization-description'))}
                            </span>
                        }
                    >
                        <SegmentedRadioGroup
                            className={styles.formButton}
                            disabled={fromDisabled}
                            options={[
                                { value: 'True', content: t('yes') },
                                { value: 'False', content: t('no') },
                            ]}
                            onUpdate={(value) => onChange('hasMonetization')(value === 'True')}
                            value={values.hasMonetization ? 'True' : 'False'}
                            size="xl"
                        />
                    </Field>
                    {isUserAdmin && (
                        <Field fieldTitle={'isTrusted'}>
                            <SegmentedRadioGroup
                                className={styles.formButton}
                                disabled={fromDisabled}
                                options={[
                                    { value: 'True', content: 'True' },
                                    { value: 'False', content: 'False' },
                                ]}
                                onUpdate={(value) => onChange('isTrusted')(value === 'True')}
                                value={values.isTrusted ? 'True' : 'False'}
                                size="xl"
                            />
                        </Field>
                    )}
                    <Field
                        required
                        fieldTitle={t('packet-name-title')}
                        fieldDescription={t('packet-name-description')}
                        error={errors.packageName}
                    >
                        <TextInput
                            className={styles.formElement}
                            disabled
                            placeholder={t('packet-name-placeholder')}
                            size="xl"
                            value={values.packageName}
                        />
                    </Field>
                    <ApkFormFields
                        apkInfos={values.apkInfosWithTargets}
                        slug={app.slug}
                        packageName={values.packageName}
                        onApkChange={onChange('apkInfosWithTargets')}
                        onPackageNameChange={
                            values.packageName
                                ? undefined
                                : (packageNameUpdated: string) => onChange('packageName')(packageNameUpdated)
                        }
                        errors={errors.apkInfosWithTargets}
                        targetPlatforms={appsMetaInfo?.targetPlatformsByChannel?.[app.channel]}
                        disabled={fromDisabled}
                    />
                </FormCard>
                <FormCard
                    cardKey="assets"
                    cardTitle={t('design')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <InstallableAppLogoUploader
                        disabled={fromDisabled}
                        logoUrl={values.logoUrl}
                        onIdChange={onChange('logoId')}
                        onUrlChange={onChange('logoUrl')}
                        setErrors={setErrors}
                    />
                    <Field
                        required
                        fieldTitle={t('icon-title')}
                        fieldDescription={t('square-icon-description')}
                        error={errors.squareLogoId}
                    >
                        <LogoUploader
                            logoType="square"
                            logoUrl={values.squareLogoUrl}
                            onIdChange={onChange('squareLogoId')}
                            onUrlChange={onChange('squareLogoUrl')}
                            setErrors={setErrors}
                            disabled={fromDisabled}
                        />
                    </Field>
                    {app.channel === Channel.SmartScreenApp && (
                        <Field
                            fieldTitle={t('secondary-icon-title')}
                            fieldDescription={t('secondary-icon-description')}
                            error={errors.secondaryLogoId}
                        >
                            <LogoUploader
                                logoType="secondary"
                                logoUrl={values.secondaryLogoUrl}
                                onIdChange={onChange('secondaryLogoId')}
                                onUrlChange={onChange('secondaryLogoUrl')}
                                setErrors={setErrors}
                                disabled={fromDisabled}
                            />
                        </Field>
                    )}
                    <BackgroundImageUploader
                        aliceApp={app}
                        values={values}
                        onIdChange={onChange('backgroundImageId')}
                        onUrlChange={onChange('backgroundImageUrl')}
                        setErrors={setErrors}
                        disabled={fromDisabled}
                    />
                    <ScreenshotsBlock
                        disabled={fromDisabled}
                        fieldTitle={t('screenshots-title')}
                        fieldDescription={t('screenshots-description')}
                        items={values.screenshots ?? []}
                        updateScreenshots={onChange('screenshots')}
                        setErrors={setErrors}
                        error={errors.screenshots}
                    />
                </FormCard>
                <FormCard
                    cardKey="agreement"
                    cardTitle={t('agreement')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        required={values.skillAccess === SkillAccess.Public}
                        fieldTitle={t('eula-title')}
                        fieldDescription={t('eula-description')}
                    >
                        <FileUploaderInput
                            disabled={fromDisabled}
                            fieldName={'eulaAgreement'}
                            onUploadComplete={onChange('eulaAgreement') as (data?: unknown) => void}
                            uploadedData={values.eulaAgreement}
                            error={errors.eulaAgreement}
                            setErrors={setErrors}
                        />
                    </Field>
                    <Field
                        required={values.skillAccess === SkillAccess.Public}
                        fieldTitle={t('private-policy-title')}
                        fieldDescription={t('private-policy-description')}
                    >
                        <FileUploaderInput
                            disabled={fromDisabled}
                            fieldName={'privacyAgreement'}
                            onUploadComplete={onChange('privacyAgreement') as (data?: unknown) => void}
                            uploadedData={values.privacyAgreement}
                            error={errors.privacyAgreement}
                            setErrors={setErrors}
                        />
                    </Field>
                </FormCard>
            </div>
        </ErrorBoundary>
    );
};

const InstallableAppForm = formContainer<OuterProps, InstallableAppFormSettings>({
    mapPropsToValues: (props) => mapInstallableAppSettingsToValues(props.settings),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const settings = mapValuesToInstallableAppSettings(values, props.app.channel);

            const result = await updateInstallableAppDraft(props.app.id, settings);

            if (result && result?.id && result?.channel && props?.setInstallableApp) {
                props.setInstallableApp(result);
                addToast(props, { title: tGeneralSettings('changes-save-success'), theme: 'success' });
            } else {
                addToast(props, { title: tGeneralSettings('error'), theme: 'danger' });
            }
        } catch (error) {
            await new Promise((resolve) => setErrors(createUpdateError(error, props.app.channel), resolve));
            addToast(props, { title: tGeneralSettings('error'), theme: 'danger' });
            console.error(error);
            throw error;
        }
    },
    setupValidationSchema: (props) => {
        const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.app.developerType);
        return {
            activationPhrases: (value: string[]) => ({
                activationPhrases: value.map((v) => validateActivationPhrase(v, validateAsAdmin, 'Активационное имя')),
            }),
            apkInfosWithTargets: (values: ApkInfoWithTargets[]) => {
                const apkUploadErrors = values?.map((value) => (!value.appVersionId ? tError('apk-upload-error') : ''));
                if (apkUploadErrors.some((error) => !!error)) return { apkInfosWithTargets: apkUploadErrors };

                const emptyPlatformErrors = values?.map((value) =>
                    !value?.targetPlatforms || value.targetPlatforms.length === 0
                        ? tError('apk-empty-platform-list')
                        : '',
                );
                if (__appConfig.user.isAdmin && emptyPlatformErrors.some((error) => !!error)) {
                    return { apkInfosWithTargets: emptyPlatformErrors };
                }

                return { apkInfosWithTargets: undefined };
            },
            brandVerificationWebsite: (value?: string) => ({
                brandVerificationWebsite: value ? validateURL(value, true) && tError('validation-url') : undefined,
            }),
            ageRestriction: (value: AgeRestriction | undefined) => {
                if (value === undefined || props.appsMetaInfo.ageRestrictions.indexOf(value) < 0) {
                    return { ageRestriction: tError('age-restriction') };
                }

                return { ageRestriction: '' };
            },
            description: (value: string) =>
                getStringFieldErrorMessage(
                    'description',
                    value,
                    tError('empty-description'),
                    lengthConfig['description'],
                    tError('too-long', { characters: lengthConfig['description'] }),
                ),
            name: (value: string) => ({
                name: value ? validateName(value, validateAsAdmin, 'Название') : tError('name-is-required'),
            }),
            noteForModerator: (value: string) =>
                getStringFieldErrorMessage(
                    'noteForModerator',
                    value,
                    '',
                    lengthConfig['noteForModerator'],
                    tError('too-long', { characters: lengthConfig['noteForModerator'] }),
                ),
            secondaryTitle: (value: string) =>
                getStringFieldErrorMessage(
                    'secondaryTitle',
                    value,
                    tError('empty-short-description'),
                    lengthConfig['secondaryTitle'],
                    tError('too-long', { characters: lengthConfig['secondaryTitle'] }),
                ),
            email: (value: string) => ({
                email: validateEmail(value) && tError('validation-email'),
            }),
        };
    },
})(InstallableAppSettingFields);

const InstallableAppWithToaster = withToaster<OuterProps & FormContainerProps>()(InstallableAppForm);

export default withAppsMetaInfo(InstallableAppWithToaster);
